/**
 * Earned Value Management (EVM) — real CPI/SPI computation.
 *
 * Phase 1 (project-level EVM): replaces the long-standing static `cpi`/`spi`
 * placeholders (which defaulted to 1.00 and were never computed) with real
 * earned-value metrics derived from data the project already holds.
 *
 * Definitions (PMI/PMBOK):
 *   BAC — Budget at Completion         = project budget
 *   AC  — Actual Cost (ACWP)           = Σ actualAmount of current-version budget lines
 *   EV  — Earned Value (BCWP)          = % complete × BAC
 *   PV  — Planned Value (BCWS)         = schedule-elapsed fraction × BAC  (linear spread)
 *   CPI = EV / AC     SPI = EV / PV
 *   CV  = EV − AC     SV  = EV − PV
 *   EAC = BAC / CPI   ETC = EAC − AC   VAC = BAC − EAC
 *
 * Transparency rule (non-negotiable for this product): when the inputs needed
 * for a metric are missing, we return it as `null` / not-available with a human
 * reason — we NEVER fall back to a flattering 1.00. The UI must show "EVM needs
 * a budget + actuals" rather than invent a healthy number.
 *
 * Phase 1 scope + decisions are documented in docs/evm-spec.md:
 *   - EV is project-level (project.completion × BAC), not task-level.
 *   - AC is the sum of current-version budget-line actuals (same source the
 *     Budget tab, Overview card and insights.ts already treat as the actual).
 *   - PV is a linear spread of BAC across the project schedule.
 *
 * Phase 2 added baseline-accurate PV (see `baselinePlannedFraction`).
 *
 * Phase 3 (audit-grade) refines EV and AC without breaking the transparency
 * rule, each with a graceful fallback reported via a `*Basis` field:
 *   - EV becomes **task-level** — a cost-weighted roll-up of each task's
 *     % complete (`taskEarnedFraction`) rather than one project-level slider —
 *     falling back to project-level completion when tasks carry no cost.
 *     `evBasis` reports 'task' | 'project'.
 *   - AC becomes **timesheet-costed** for labor — logged hours × resolved rate
 *     (computed upstream by `computeTimesheetLaborCost`, passed in as
 *     `opts.laborActualCost`) blended with non-labor budget-line actuals — and
 *     falls back to all budget-line actuals when no fully-rated logged time
 *     exists. `acBasis` reports 'timesheet' | 'budgetLines'. The blend NEVER
 *     drops non-labor spend, so it can't silently understate AC (flatter CPI).
 */

import type { Project, Task } from '@/types';

export interface EvmMetrics {
  bac: number;
  ev: number;
  pv: number;
  ac: number;
  /** Cost Variance = EV − AC (positive = under budget). */
  cv: number;
  /** Schedule Variance = EV − PV (positive = ahead of schedule). */
  sv: number;
  /** Cost Performance Index = EV / AC. null when AC is unavailable. */
  cpi: number | null;
  /** Schedule Performance Index = EV / PV. null when PV is unavailable. */
  spi: number | null;
  /** Estimate at Completion = BAC / CPI. null when CPI is unavailable. */
  eac: number | null;
  /** Estimate to Complete = EAC − AC. */
  etc: number | null;
  /** Variance at Completion = BAC − EAC. */
  vac: number | null;
  /** True once there is enough data to show cost performance (CPI/EAC). */
  cpiAvailable: boolean;
  /** True once there is enough data to show schedule performance (SPI). */
  spiAvailable: boolean;
  /**
   * How Planned Value was derived:
   *  - 'baseline' — from the active baseline's time-phased schedule (Phase 2)
   *  - 'linear'   — linear calendar spread of BAC (Phase 1 fallback)
   *  - 'none'     — not computable (no baseline, no valid schedule)
   */
  pvBasis: 'baseline' | 'linear' | 'none';
  /**
   * How Earned Value was derived (Phase 3):
   *  - 'task'    — cost-weighted roll-up of each task's % complete
   *  - 'project' — project.completion × BAC (fallback; no task costs)
   */
  evBasis: 'task' | 'project';
  /**
   * How Actual Cost was derived (Phase 3):
   *  - 'timesheet'   — logged labor hours × rate + non-labor budget-line actuals
   *  - 'budgetLines' — sum of current-version budget-line actuals (fallback)
   */
  acBasis: 'timesheet' | 'budgetLines';
  /** Human-readable reasons a metric can't be shown, for the UI gate. */
  missing: string[];
}

type EvmProject = Pick<
  Project,
  | 'budget'
  | 'completion'
  | 'startDate'
  | 'endDate'
  | 'budgetLines'
  | 'currentBudgetVersionId'
  | 'baselines'
  | 'activeBaselineId'
  | 'wbsTasks'
>;

/** Options for Phase 3 refinements. All optional — omitting them yields the
 *  Phase 1/2 behavior (budget-line AC). */
export interface EvmOptions {
  /**
   * Timesheet-costed labor actual (Σ logged hours × resolved rate), computed
   * upstream by `computeTimesheetLaborCost` where the store + rate resolver
   * live. Pass it ONLY when every logged hour is rated (see `fullyRated`),
   * so blending it with non-labor budget-line actuals can't understate AC.
   * A number here switches AC to the timesheet basis; null/undefined keeps the
   * budget-line basis.
   */
  laborActualCost?: number | null;
}

/** Parse a 'YYYY-MM-DD' (or ISO) date string as LOCAL midnight. Mirrors formatDate. */
function parseLocalDate(s: string | null | undefined): Date | null {
  if (!s) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (!m) {
    const d = new Date(s);
    return isNaN(d.getTime()) ? null : d;
  }
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

/**
 * Actual Cost = Σ actualAmount across the project's CURRENT budget version.
 * Matches src/lib/insights.ts (ruleActualExceedsBudget) and the Budget tab,
 * so EVM, insights and the budget UI all agree on "actual".
 */
export function actualCost(project: EvmProject): number {
  const lines = project.budgetLines ?? [];
  const vid = project.currentBudgetVersionId;
  const active = vid ? lines.filter((l) => l.budgetVersionId === vid) : lines;
  return active.reduce((sum, l) => sum + (l.actualAmount || 0), 0);
}

/**
 * Non-labor Actual Cost = Σ actualAmount across current-version budget lines
 * that are NOT flagged as labor. Used by the Phase 3 "labor-aware blend": when
 * labor cost comes from timesheets, we must still count material / license /
 * other non-labor spend so AC is never understated. A line with `isLabor`
 * undefined is treated as non-labor (conservative — it stays in AC rather than
 * silently disappearing), so an unflagged labor line over-counts rather than
 * under-counts, which errs toward a worse CPI, never a flattering one.
 */
export function nonLaborActualCost(project: EvmProject): number {
  const lines = project.budgetLines ?? [];
  const vid = project.currentBudgetVersionId;
  const active = vid ? lines.filter((l) => l.budgetVersionId === vid) : lines;
  return active
    .filter((l) => l.isLabor !== true)
    .reduce((sum, l) => sum + (l.actualAmount || 0), 0);
}

/**
 * Phase 3 — task-level Earned Value as a cost-weighted fraction of completion.
 * Each non-summary task contributes `cost × (%complete/100)`; the fraction is
 * `Σ earned / Σ cost` over tasks that carry a cost. Returns null when no task
 * carries a cost (caller falls back to project-level completion). Mirrors
 * `baselinePlannedFraction` so EV and PV are measured the same way — the
 * fraction is ∈ [0,1], so `EV = BAC × fraction` stays on the BAC scale and
 * CPI/SPI/EAC remain consistent regardless of how task costs sum.
 */
export function taskEarnedFraction(
  tasks: ReadonlyArray<Pick<Task, 'type' | 'cost' | 'percentComplete'>> | null | undefined,
): number | null {
  if (!tasks?.length) return null;
  let earned = 0;
  let total = 0;
  for (const t of tasks) {
    if (t.type === 'summary') continue; // summary rows roll up children — don't double count
    const cost = t.cost || 0;
    if (cost <= 0) continue;
    const pct = Math.max(0, Math.min(100, t.percentComplete ?? 0)) / 100;
    earned += cost * pct;
    total += cost;
  }
  return total > 0 ? earned / total : null;
}

/**
 * Project-level % complete (0–100) rolled up from leaf (non-summary) tasks, for
 * the stored `project.completion` field. Weighting mirrors `taskEarnedFraction`
 * and the baseline logic: cost-weighted when any leaf carries a cost, otherwise
 * duration-weighted, otherwise an equal-weight average. Unlike
 * `taskEarnedFraction` it counts every leaf task (not only costed ones), since
 * overall progress should reflect the whole schedule. Returns null when there
 * are no non-summary tasks, so callers keep any existing manual value.
 */
export function rollUpCompletion(
  tasks: ReadonlyArray<Pick<Task, 'type' | 'cost' | 'duration' | 'percentComplete'>> | null | undefined,
): number | null {
  const leaves = (tasks ?? []).filter((t) => t.type !== 'summary');
  if (leaves.length === 0) return null;
  const totalCost = leaves.reduce((s, t) => s + (t.cost || 0), 0);
  const totalDuration = leaves.reduce((s, t) => s + Math.max(0, t.duration || 0), 0);
  const basis: 'cost' | 'duration' | 'equal' =
    totalCost > 0 ? 'cost' : totalDuration > 0 ? 'duration' : 'equal';
  let weighted = 0;
  let totalWeight = 0;
  for (const t of leaves) {
    const pct = Math.min(100, Math.max(0, t.percentComplete ?? 0));
    const w = basis === 'cost' ? t.cost || 0 : basis === 'duration' ? Math.max(0, t.duration || 0) : 1;
    if (w <= 0) continue;
    weighted += w * pct;
    totalWeight += w;
  }
  return totalWeight > 0 ? Math.round(weighted / totalWeight) : 0;
}

/** Planned (BAC) — project.budget, falling back to the sum of planned budget lines. */
export function budgetAtCompletion(project: EvmProject): number {
  if (project.budget && project.budget > 0) return project.budget;
  const lines = project.budgetLines ?? [];
  const vid = project.currentBudgetVersionId;
  const active = vid ? lines.filter((l) => l.budgetVersionId === vid) : lines;
  return active.reduce((sum, l) => sum + (l.plannedAmount || 0), 0);
}

/**
 * Fraction of the planned schedule elapsed as of `asOf` (default: now), clamped
 * to [0, 1]. Used to spread BAC into a linear Planned Value. Returns null when
 * the schedule window is missing or degenerate (no honest PV possible).
 */
export function scheduleElapsedFraction(
  startDate: string | null | undefined,
  endDate: string | null | undefined,
  asOf: Date = new Date(),
): number | null {
  const start = parseLocalDate(startDate);
  const end = parseLocalDate(endDate);
  if (!start || !end) return null;
  const span = end.getTime() - start.getTime();
  if (span <= 0) return null;
  const frac = (asOf.getTime() - start.getTime()) / span;
  return Math.max(0, Math.min(1, frac));
}

/** Planned fraction of a single task's baseline window elapsed by `asOf`.
 *  A zero-length window (milestone) is a step: 0 before its date, 1 on/after. */
function taskPlannedFraction(
  startStr: string | null | undefined,
  endStr: string | null | undefined,
  asOf: Date,
): number | null {
  const start = parseLocalDate(startStr);
  const end = parseLocalDate(endStr);
  if (!start || !end) return null;
  const span = end.getTime() - start.getTime();
  if (span <= 0) return asOf.getTime() >= start.getTime() ? 1 : 0;
  return Math.max(0, Math.min(1, (asOf.getTime() - start.getTime()) / span));
}

/**
 * Phase 2 — Planned Value fraction from the ACTIVE BASELINE's time-phased
 * schedule, so SPI is measured against the real plan rather than a flat
 * calendar line. Each baseline task contributes its planned-elapsed fraction,
 * weighted by baseline cost when any baseline costs are recorded, otherwise by
 * baseline duration (schedule-accurate even before task-level costing exists).
 * Returns null when there is no usable baseline (caller falls back to linear).
 */
export function baselinePlannedFraction(project: EvmProject, asOf: Date = new Date()): number | null {
  const baseline = (project.baselines ?? []).find((b) => b.id === project.activeBaselineId);
  const tasks = baseline?.snapshot?.wbsTasks ?? [];
  if (!tasks.length) return null;

  const totalCost = tasks.reduce((s, t) => s + (t.baselineCost || 0), 0);
  const useCost = totalCost > 0;

  let done = 0;
  let total = 0;
  for (const t of tasks) {
    const frac = taskPlannedFraction(t.baselineStartDate, t.baselineEndDate, asOf);
    if (frac === null) continue; // skip tasks without a usable baseline window
    const weight = useCost ? t.baselineCost || 0 : Math.max(t.baselineDuration || 0, 0);
    if (weight <= 0) continue;
    done += weight * frac;
    total += weight;
  }
  return total > 0 ? done / total : null;
}

export interface TimesheetLaborCost {
  /** Σ logged hours × resolved hourly rate, over users with a rate. */
  laborCost: number;
  /** Hours logged by users that resolved to a rate (counted in laborCost). */
  ratedHours: number;
  /** Hours logged by users with no resolvable rate (a costing gap). */
  unratedHours: number;
  /**
   * True only when there is logged time AND every logged hour is rated. The
   * caller passes `laborCost` into EVM as `laborActualCost` ONLY when this is
   * true — otherwise timesheet-costed AC would omit the unrated hours and
   * understate the actual, flattering CPI.
   */
  fullyRated: boolean;
}

/**
 * Phase 3 — cost logged timesheet hours. Pure: the caller supplies hours per
 * user (auth user id) and a `rateForUser` resolver (built upstream from the
 * resource → rate-card stack and the user→resource bridge, which live in the
 * store, not here — keeping this module store-free and testable).
 */
export function computeTimesheetLaborCost(
  hoursByUser: Record<string, number>,
  rateForUser: (userId: string) => number | null,
): TimesheetLaborCost {
  let laborCost = 0;
  let ratedHours = 0;
  let unratedHours = 0;
  for (const [userId, hours] of Object.entries(hoursByUser)) {
    const h = hours || 0;
    if (h <= 0) continue;
    const rate = rateForUser(userId);
    if (rate === null || rate <= 0) {
      unratedHours += h;
      continue;
    }
    laborCost += h * rate;
    ratedHours += h;
  }
  return {
    laborCost,
    ratedHours,
    unratedHours,
    fullyRated: ratedHours > 0 && unratedHours === 0,
  };
}

/**
 * Compute real EVM metrics for a project. Pure and side-effect free.
 * `asOf` lets callers compute "as of" a reporting date (default: today).
 * `opts.laborActualCost` (Phase 3) switches AC to the timesheet basis.
 */
export function computeEvm(
  project: EvmProject,
  asOf: Date = new Date(),
  opts: EvmOptions = {},
): EvmMetrics {
  const bac = budgetAtCompletion(project);

  // Earned Value: prefer a cost-weighted task roll-up (Phase 3); fall back to
  // the project-level completion slider when no task carries a cost.
  const completion = Math.max(0, Math.min(100, project.completion ?? 0));
  const taskFrac = taskEarnedFraction(project.wbsTasks);
  const evFrac = taskFrac !== null ? taskFrac : completion / 100;
  const evBasis: EvmMetrics['evBasis'] = taskFrac !== null ? 'task' : 'project';
  const ev = bac * evFrac;

  // Actual Cost: prefer the timesheet-costed labor blend (Phase 3) when the
  // caller supplies a fully-rated labor cost; otherwise sum budget-line actuals.
  const labor = opts.laborActualCost;
  const useTimesheet = typeof labor === 'number' && labor >= 0;
  const ac = useTimesheet ? labor + nonLaborActualCost(project) : actualCost(project);
  const acBasis: EvmMetrics['acBasis'] = useTimesheet ? 'timesheet' : 'budgetLines';

  // Planned Value: prefer the active baseline's time-phased schedule (Phase 2);
  // fall back to a linear calendar spread of BAC when there's no usable baseline.
  const baselineFrac = baselinePlannedFraction(project, asOf);
  const linearFrac = scheduleElapsedFraction(project.startDate, project.endDate, asOf);
  const pvFrac = baselineFrac !== null ? baselineFrac : linearFrac;
  const pvBasis: EvmMetrics['pvBasis'] =
    baselineFrac !== null ? 'baseline' : linearFrac !== null ? 'linear' : 'none';
  const pv = pvFrac === null ? 0 : bac * pvFrac;

  const missing: string[] = [];
  const hasBudget = bac > 0;
  if (!hasBudget) missing.push('Set a budget (or add budget lines) to enable EVM.');

  // Cost performance needs a budget AND some actual cost recorded.
  const cpiAvailable = hasBudget && ac > 0;
  if (hasBudget && ac <= 0) missing.push('Add actual costs on the Budget tab to compute CPI.');

  // Schedule performance needs a budget AND a usable planned-value basis
  // (a baseline schedule, or at least valid project start/end dates).
  const spiAvailable = hasBudget && pvFrac !== null;
  if (hasBudget && pvFrac === null) {
    missing.push('Set valid start and end dates (or a baseline) to compute SPI.');
  }

  const cpi = cpiAvailable ? ev / ac : null;
  const spi = spiAvailable && pv > 0 ? ev / pv : null;
  const eac = cpi && cpi > 0 ? bac / cpi : null;
  const etc = eac !== null ? eac - ac : null;
  const vac = eac !== null ? bac - eac : null;

  return {
    bac,
    ev,
    pv,
    ac,
    cv: ev - ac,
    sv: ev - pv,
    cpi,
    spi,
    eac,
    etc,
    vac,
    cpiAvailable,
    spiAvailable,
    pvBasis,
    evBasis,
    acBasis,
    missing,
  };
}
